/* eslint-disable */
import DeleteIcon from '@mui/icons-material/Delete';
import DoneIcon from '@mui/icons-material/Done';
import EditIcon from '@mui/icons-material/Edit';
import SendIcon from '@mui/icons-material/Send';
import GppMaybeOutlined from '@mui/icons-material/GppMaybeOutlined';
import { Box, Button, Chip, Typography, useTheme } from '@mui/material';
import {
  GridSortModel,
  type GridColumns,
  type GridValidRowModel,
} from '@mui/x-data-grid';
import { DialogBox } from 'components/shared';
import { strings } from 'i18n/Strings';
import { Fragment, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getAllOrganizationAdmins,
  removeEmployee,
  updateEmployee,
} from 'service/UserService';
import { setSnackBarFailed, setSnackBarSuccess } from 'store/snackBar';
import { colorTheme } from '../../../theme';
import AlertModal from '../../../components/shared/AlertModal';
import CreateAdminForm from './CreateAdmin';
import UpdateAdminForm from './UpdateAdmin';
import { forgotPassword } from 'service';
import { type RootState } from 'store/rootReducer';
import { CustomDataGrid } from 'components';
import { Switch } from '@mui/material';
import Tooltip from '@mui/material/Tooltip/Tooltip';
import { roles } from 'constants/constantText';
import {
  setCurrentPage,
  setSortBy,
  setSortOrder,
} from 'store/component/pagination';
import { checkSortingValues } from 'utils/GeneralUtil';
import ConfirmationModal from 'components/confirmation-modal';
import CreateEmployee, {
  IEmployeeInfo,
} from 'pages/Employer/Employees/CreateEmployee';

type GridRowData = Record<string, unknown>;

const Users = (): JSX.Element => {
  const currentTheme = useTheme();
  const dispatch = useDispatch();
  const colors = colorTheme(currentTheme.palette.mode);
  const [openCreateDialog, setOpenCreateDialog] = useState<boolean>(false);
  const [openUpdateDialog, setOpenUpdateDialog] = useState<boolean>(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const [confirmationModal, setConfirmationModal] = useState<boolean>(false);
  const [employerAdminData, setEmployerAdminData] = useState<GridRowData[]>([]);
  const [selectedData, setSelectedData] = useState<
    GridValidRowModel | undefined
  >();
  const [pageCount, setPageCount] = useState(null);
  const { orgId } = useSelector((state: RootState) => state.onboarding);
  const ref = useRef<HTMLInputElement>(null);
  const pageInfo = useSelector((state) => state?.pagination?.employees);
  const { userDetails } = useSelector((state: RootState) => state.auth);

  const [searchValue, setSearchValue] = useState('');
  const getEmployeeData = (): void => {
    getAllOrganizationAdmins({
      id: orgId.toString(),
      roleName: roles.OrganizationAdminRole,
      page: Number(pageInfo?.page) + 1,
      size: pageInfo?.pageSize,
      sortBy: pageInfo?.sortBy,
      sortOrder: pageInfo?.sortOrder,
      searchValue,
    })
      .then((res) => {
        if (typeof res !== 'undefined' && 'basicInfo' in res) {
          const { basicInfo } = res as { basicInfo: GridRowData[] };
          if (basicInfo) {
            setEmployerAdminData(basicInfo?.rows);
            setPageCount(basicInfo?.count);
          }
        }
      })
      .catch((err: Error) => {
        throw err;
      });
  };

  useEffect(() => {
    if (orgId) {
      getEmployeeData();
    }
  }, [
    pageInfo?.page,
    pageInfo.pageSize,
    pageInfo?.sortBy,
    pageInfo?.sortOrder,
    searchValue,
    orgId,
  ]);

  const handleResentInvite = (email: string): void => {
    if (email != null) {
      void (async () => {
        await forgotPassword(email)
          .then(() => {
            dispatch(
              setSnackBarSuccess({
                snackBarMessage: strings.displayText.resetLinkSuccessText,
              }),
            );
          })
          .catch((err) => {
            dispatch(
              setSnackBarFailed({ snackBarMessage: err.response.data.message }),
            );
          });
      })();
    }
  };
  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    row: GridValidRowModel,
  ): void => {
    void (async () => {
      // Update employee status
      updateEmployee(Number(row?.empId), {
        isActive: !row.isActive,
      })
        .then((res) => {
          dispatch(
            setSnackBarSuccess({
              snackBarMessage: `${strings.displayText.adminStatusChange} ${
                !row.isActive ? 'active' : 'in-active'
              }`,
            }),
          );
          res && getEmployeeData();
        })
        .catch((err) => {
          dispatch(
            setSnackBarFailed({
              snackBarMessage: err.response.data.message,
            }),
          );
          console.log(err);
        });
    })();

    event.stopPropagation();
  };
  const columns: GridColumns<GridValidRowModel> = [
    {
      field: 'firstName',
      flex: 0.7,
      cellClassName: 'name-column--cell',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.firstName}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.firstName}
        </Typography>
      ),
    },
    {
      field: 'lastName',
      flex: 0.7,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.lastName}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.lastName}
        </Typography>
      ),
    },
    {
      field: 'email',
      flex: 1.1,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.emailAddress}
        </Typography>
      ),
      renderCell: ({ row }) => {
        const maxLength = 23; // Adjust as needed
        const email = row.email;
        const truncatedEmail =
          email.length > maxLength ? email.slice(0, maxLength) + '...' : email;

        return (
          <Tooltip title={email} placement="top">
            <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
              {truncatedEmail}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'resendInvite',
      headerName: 'Resend Invite',
      headerAlign: 'center',
      sortable: false,
      flex: 0.8,
      renderCell: ({ row }) => (
        <Box m="0 auto" p="5px" display="flex" justifyContent="space-evenly">
          {!row.emailVerifiedAt ? (
            <Button
              sx={{ padding: '4px 5px' }}
              variant="outlined"
              color="success"
              disabled={false}
              onClick={() => {
                handleResentInvite(row.email);
              }}
              endIcon={<SendIcon />}
            >
              Invite
            </Button>
          ) : (
            <Button
              sx={{ padding: '4px 5px' }}
              variant="outlined"
              color="success"
              disabled={true}
              endIcon={<SendIcon />}
            >
              Invite
            </Button>
          )}
        </Box>
      ),
    },

    {
      field: 'active',
      flex: 0.5,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          Active
        </Typography>
      ),

      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          <Box
            onClick={(e: React.ChangeEvent<HTMLInputElement>) => {
              if (row.empId !== userDetails?.empId) handleChange(e, row);
            }}
          >
            <Switch
              disabled={row.empId === userDetails?.empId}
              checked={row.isActive || false}
              inputProps={{ 'aria-label': 'controlled' }}
            />
          </Box>
        </Typography>
      ),
    },
  ];

  const handleCreateDialogClose = (): void => {
    setConfirmationModal(true);
  };

  const handleCloseAdminDialogBox = (): void => {
    setOpenCreateDialog(false);
  };

  const handleUpdateDialogClose = (): void => {
    setOpenUpdateDialog(false);
  };

  const handleDeleteDialogClose = (): void => {
    setOpenDeleteDialog(false);
  };

  const handleDelete = async (): Promise<void> => {
    if (selectedData?.empId !== undefined) {
      const res = await removeEmployee(Number(selectedData.empId)); // convert to number
      if (res) {
        dispatch(
          setSnackBarSuccess({
            snackBarMessage: res?.msg,
          }),
        );
      } else {
        dispatch(setSnackBarFailed({ snackBarMessage: 'User deleted failed' }));
      }

      res && getEmployeeData();
      setOpenDeleteDialog(false);
    }
  };

  const handleSearch = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    setSearchValue(event.target.value);
    dispatch(setCurrentPage({ key: 'employees', value: 0 }));
  };

  const handleSortChange = (model: GridSortModel): void => {
    dispatch(
      setSortBy({
        key: 'employees',
        value: model[0].field,
      }),
    );
    dispatch(setSortOrder({ key: 'employees', value: model[0].sort }));
    dispatch(setCurrentPage({ key: 'employees', value: 0 }));
  };

  const handleConfirmationClose = () => {
    setOpenCreateDialog(false);
    setConfirmationModal(false);
  };

  const handleContinuePress = () => {
    setConfirmationModal(false);
  };

  return (
    <Fragment>
      <CustomDataGrid
        title="Admins"
        columns={columns}
        rows={employerAdminData}
        searchEnabled={true}
        handleSearch={handleSearch}
        addButtonTitle={'Add Admin'}
        onAddButtonClick={() => {
          setOpenCreateDialog(true);
          ref.current?.focus();
        }}
        searchPlaceholder={strings.displayText.searchLabel1}
        actionBtnText1="edit"
        actionBtnText2="delete"
        ActionBtnIcon1={() => <EditIcon />}
        ActionBtnIcon2={() => <DeleteIcon />}
        onActionBtnClick1={(row) => {
          setSelectedData(row);
          setOpenUpdateDialog(true);
        }}
        onActionBtnClick2={(row, event) => {
          if (userDetails.empId === row.empId) {
            event.stopPropagation();
            dispatch(
              setSnackBarFailed({
                snackBarMessage: strings.displayText.sameUserCantDelete,
              }),
            );
            return;
          }

          setSelectedData(row);
          setOpenDeleteDialog(true);
        }}
        getRowId={(row: GridRowData) =>
          `${String(row.empId)}${row.email ? row.email : ''}`
        }
        getRowClassName={(params) =>
          params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
        }
        currentPage="employees"
        pageCount={pageCount}
        sortModel={checkSortingValues(pageInfo)}
        sortingMode={strings.displayText.sortingServer}
        onSortModelChange={(mode) => {
          handleSortChange(mode);
        }}
      />

      <DialogBox
        open={openCreateDialog}
        onClose={handleCreateDialogClose}
        screenWidth="md"
        title={strings.displayText.createAdmin}
      >
        <CreateEmployee
          organizationId={`${orgId}`}
          from="admin"
          updateEmployeeData={getEmployeeData}
          onClose={handleCloseAdminDialogBox}
          page="Create Admin"
          hidePhoneField={true}
        />
      </DialogBox>

      <DialogBox
        open={openUpdateDialog}
        onClose={handleUpdateDialogClose}
        screenWidth="md"
        title={strings.displayText.editAdmin}
      >
        <CreateEmployee
          updateEmployeeId={selectedData?.empId}
          from="admin"
          updateEmployeeData={getEmployeeData}
          onClose={handleUpdateDialogClose}
          page="Edit Admin"
          organizationId={`${selectedData?.organizationId}`}
          selectedData={selectedData as IEmployeeInfo}
          hidePhoneField={true}
        />
      </DialogBox>
      <AlertModal
        open={openDeleteDialog}
        onClose={handleDeleteDialogClose}
        handleDelete={handleDelete}
        isShowAlertMessage={employerAdminData?.length === 1}
        title={
          employerAdminData?.length === 1
            ? strings.displayText.deleteAlertMessage
            : `${strings.displayText.deleteConfirmationBAdminText} admin`
        }
        email={String(selectedData?.email)}
      />
      <ConfirmationModal
        open={confirmationModal}
        onClose={handleConfirmationClose}
        onContinue={handleContinuePress}
      />
    </Fragment>
  );
};

export default Users;
